/**
 * OnboardingTopIdeas
 *
 * Screen 4 of the onboarding flow.
 * Shows AI portfolio holdings + investor-type picks. User taps to add to watchlist.
 */
import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { colors, fontStyles, spacing } from '../../../../styles';
import {
  getInvestingArenaPositionsOrThrow,
  InvestingArenaPosition,
  AIModelId,
  AI_MODEL_ICONS,
} from '../../../../apis/investingArena';
import { InvestorType } from '../../../../utils/smartChatPrompts';

// ─── Curated investor-type picks (hardcoded, can be replaced with API later) ─────

const INVESTOR_TYPE_PICKS: Record<string, Array<{ symbol: string; name: string }>> = {
  'new-investor': [
    { symbol: 'VOO', name: 'Vanguard S&P 500 ETF' },
    { symbol: 'AAPL', name: 'Apple' },
    { symbol: 'MSFT', name: 'Microsoft' },
  ],
  'active-trader': [
    { symbol: 'NVDA', name: 'NVIDIA' },
    { symbol: 'TSLA', name: 'Tesla' },
    { symbol: 'PLTR', name: 'Palantir' },
  ],
  researcher: [
    { symbol: 'GOOGL', name: 'Alphabet' },
    { symbol: 'META', name: 'Meta' },
    { symbol: 'AMZN', name: 'Amazon' },
  ],
  other: [
    { symbol: 'SPY', name: 'S&P 500 ETF' },
    { symbol: 'AAPL', name: 'Apple' },
    { symbol: 'NVDA', name: 'NVIDIA' },
  ],
};

// ─── Section header labels ─────────────────────────────────────────────────────

function getSectionTwoHeaderKey(investorType: InvestorType | undefined): string {
  switch (investorType) {
    case 'new-investor':
      return 'onboarding.topIdeasSectionNewInvestor';
    case 'active-trader':
      return 'onboarding.topIdeasSectionActiveTrader';
    case 'researcher':
      return 'onboarding.topIdeasSectionResearcher';
    default:
      return 'onboarding.topIdeasSectionDefault';
  }
}

// ─── AI model icon circle ──────────────────────────────────────────────────────

const AIModelIcon: React.FC<{ modelId: AIModelId; stacked?: boolean }> = ({ modelId, stacked }) => (
  <div
    css={css`
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: white;
      border: ${stacked ? '1.5px solid var(--card-background)' : `1px solid ${colors.cloud}`};
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      html[data-theme='dark'] & {
        background: #2a2a2a;
        border-color: ${stacked ? 'var(--card-background)' : colors.tungsten};
      }
    `}
    aria-label={modelId}
  >
    <img
      src={AI_MODEL_ICONS[modelId]}
      alt={modelId}
      css={css`
        width: 13px;
        height: 13px;
        object-fit: contain;
        html[data-theme='dark'] & {
          filter: ${modelId === 'openai' ? 'invert(1)' : 'none'};
        }
      `}
    />
  </div>
);

// ─── Stock row ──────────────────────────────────────────────────────────────────

interface StockRowProps {
  symbol: string;
  name?: string;
  aiModels?: AIModelId[];
  isSelected: boolean;
  onToggle: (symbol: string) => void;
  inCard?: boolean;
}

const StockRow: React.FC<StockRowProps> = ({ symbol, name, aiModels, isSelected, onToggle, inCard }) => {
  return (
    <button
      onClick={() => onToggle(symbol)}
      css={css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: ${spacing.centi} ${spacing.deci};
        background: ${isSelected && inCard ? colors.skyAir : 'var(--card-background)'};
        border: ${inCard ? 'none' : `1.5px solid ${isSelected ? colors.petrolBlue : colors.cloud}`};
        border-radius: ${inCard ? 0 : '8px'};
        cursor: pointer;
        text-align: left;
        transition:
          border-color 0.15s ease,
          background 0.15s ease;

        html[data-theme='dark'] & {
          background: ${isSelected && inCard ? `${colors.skyBlue}18` : 'var(--card-background)'};
          ${!inCard ? `border-color: ${isSelected ? colors.skyBlue : colors.tungsten};` : ''}
        }

        &:active {
          opacity: 0.85;
        }
      `}
      data-testid={`stock-row-${symbol}`}
    >
      {/* Left: symbol + name + AI tags */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
          min-width: 0;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.centi};
            flex-wrap: wrap;
          `}
        >
          <span
            css={css`
              ${fontStyles.dog}
              font-weight: 700;
              color: ${colors.liquorice};
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {symbol}
          </span>
          {aiModels && aiModels.length > 0 && (
            <div
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              {aiModels.map((m, i) => (
                <div
                  key={m}
                  css={css`
                    margin-left: ${i === 0 ? 0 : -6}px;
                    z-index: ${aiModels.length - i};
                    position: relative;
                  `}
                >
                  <AIModelIcon modelId={m} stacked={aiModels.length > 1} />
                </div>
              ))}
            </div>
          )}
        </div>
        {name && (
          <span
            css={css`
              ${fontStyles.flea}
              color: ${colors.silver};
            `}
          >
            {name}
          </span>
        )}
      </div>

      {/* Right: add / check button */}
      <div
        css={css`
          flex-shrink: 0;
          margin-left: ${spacing.centi};
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: ${isSelected ? colors.petrolBlue : 'transparent'};
          border: 1.5px solid ${isSelected ? colors.petrolBlue : `${colors.petrolBlue}55`};
          transition: all 0.15s ease;
          html[data-theme='dark'] & {
            background: ${isSelected ? colors.skyBlue : 'transparent'};
            border-color: ${isSelected ? colors.skyBlue : `${colors.skyBlue}55`};
          }
        `}
        aria-label={isSelected ? `Remove ${symbol}` : `Add ${symbol}`}
      >
        {isSelected ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M2.5 7L5.5 10L11.5 4"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 3V11M3 7H11" stroke={colors.petrolBlue} strokeWidth="2" strokeLinecap="round" />
          </svg>
        )}
      </div>
    </button>
  );
};

// ─── Skeleton loader ────────────────────────────────────────────────────────────

const skeletonPulse = css`
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  animation: pulse 1.5s ease-in-out infinite;
`;

const SkeletonRow: React.FC = () => (
  <div
    css={css`
      height: 62px;
      border-radius: 8px;
      background: ${colors.snow};
      border: 0.5px solid ${colors.cloud};
      ${skeletonPulse}
      html[data-theme='dark'] & {
        background: var(--background-color);
        border-color: ${colors.tungsten};
      }
    `}
  />
);

// ─── Section label ──────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode; accent?: boolean }> = ({ children, accent }) => (
  <p
    css={css`
      ${fontStyles.flea}
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: ${accent ? colors.petrolBlue : colors.tungsten};
      margin: 0 0 ${spacing.centi};
    `}
  >
    {children}
  </p>
);

// ─── Main component ─────────────────────────────────────────────────────────────

interface OnboardingTopIdeasProps {
  investorType?: InvestorType;
  userSymbols: string[];
  onSymbolsChange: (symbols: string[]) => void;
}

/**
 * Merges positions from all 3 AI portfolios into a deduplicated list,
 * annotating each symbol with which AI(s) hold it.
 */
function mergeAIHoldings(
  openai: InvestingArenaPosition[],
  gemini: InvestingArenaPosition[],
  claude: InvestingArenaPosition[],
): Array<{ symbol: string; models: AIModelId[] }> {
  const map = new Map<string, AIModelId[]>();

  const add = (positions: InvestingArenaPosition[], modelId: AIModelId) => {
    for (const pos of positions) {
      const sym = pos.symbol.toUpperCase();
      const existing = map.get(sym) ?? [];
      map.set(sym, [...existing, modelId]);
    }
  };

  add(openai, 'openai');
  add(gemini, 'gemini');
  add(claude, 'claude');

  // Sort: stocks held by all 3 first, then 2, then 1
  return [...map.entries()]
    .sort((a, b) => b[1].length - a[1].length)
    .slice(0, 5) // show top 5
    .map(([symbol, models]) => ({ symbol, models }));
}

const OnboardingTopIdeas: React.FC<OnboardingTopIdeasProps> = ({ investorType, userSymbols, onSymbolsChange }) => {
  const { t } = useTranslation();

  // Fetch positions for all 3 AI portfolios in parallel.
  // Use getInvestingArenaPositionsOrThrow so React Query sets isError on failure —
  // this lets us distinguish a real API error from a genuinely empty portfolio.
  const {
    data: openaiPositions = [],
    isLoading: openaiLoading,
    isError: openaiError,
  } = useQuery(
    ['investing-arena', 'positions', 'openai', 'strict'],
    () => getInvestingArenaPositionsOrThrow('openai'),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      retry: 0,
    },
  );
  const {
    data: geminiPositions = [],
    isLoading: geminiLoading,
    isError: geminiError,
  } = useQuery(
    ['investing-arena', 'positions', 'gemini', 'strict'],
    () => getInvestingArenaPositionsOrThrow('gemini'),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      retry: 0,
    },
  );
  const {
    data: claudePositions = [],
    isLoading: claudeLoading,
    isError: claudeError,
  } = useQuery(
    ['investing-arena', 'positions', 'claude', 'strict'],
    () => getInvestingArenaPositionsOrThrow('claude'),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      retry: 0,
    },
  );

  const positionsLoading = openaiLoading || geminiLoading || claudeLoading;
  const positionsError = openaiError || geminiError || claudeError;

  const aiHoldings = useMemo(
    () => mergeAIHoldings(openaiPositions, geminiPositions, claudePositions),
    [openaiPositions, geminiPositions, claudePositions],
  );

  // Curated investor-type picks (deduplicated from AI holdings if needed)
  const curatedPicks = useMemo(() => {
    const picks = INVESTOR_TYPE_PICKS[investorType ?? 'other'] ?? INVESTOR_TYPE_PICKS['other'];
    const aiSymbols = new Set(aiHoldings.map((h) => h.symbol));
    // Remove from curated if already in AI holdings to avoid duplication
    return picks.filter((p) => !aiSymbols.has(p.symbol));
  }, [investorType, aiHoldings]);

  const toggleSymbol = (symbol: string) => {
    if (userSymbols.includes(symbol)) {
      onSymbolsChange(userSymbols.filter((s) => s !== symbol));
    } else {
      onSymbolsChange([...userSymbols, symbol]);
    }
  };

  const sectionTwoHeader = t(getSectionTwoHeaderKey(investorType));

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.deci};
      `}
      data-testid="top-ideas-container"
    >
      {/* Section 1: AI portfolios are buying */}
      <div>
        <SectionLabel accent>{t('onboarding.topIdeasAISection')}</SectionLabel>
        <div
          css={css`
            border-radius: 8px;
            border: 0.5px solid ${colors.cloud};
            overflow: hidden;
            html[data-theme='dark'] & {
              border-color: ${colors.tungsten};
            }
          `}
          data-testid="ai-holdings-section"
        >
          {positionsLoading ? (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${spacing.centi};
                padding: ${spacing.centi};
              `}
            >
              {[1, 2, 3].map((i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          ) : positionsError ? (
            <p
              css={css`
                ${fontStyles.cat}
                color: ${colors.silver};
                text-align: center;
                padding: ${spacing.deci};
              `}
              data-testid="ai-holdings-error"
            >
              {t('onboarding.aiArenaLoadError')}
            </p>
          ) : aiHoldings.length === 0 ? (
            <p
              css={css`
                ${fontStyles.cat}
                color: ${colors.silver};
                text-align: center;
                padding: ${spacing.deci};
              `}
            >
              {t('onboarding.topIdeasNoHoldings')}
            </p>
          ) : (
            aiHoldings.map(({ symbol, models }, idx) => (
              <React.Fragment key={symbol}>
                {idx > 0 && (
                  <div
                    css={css`
                      height: 0.5px;
                      background: ${colors.cloud};
                      html[data-theme='dark'] & {
                        background: ${colors.tungsten};
                      }
                    `}
                  />
                )}
                <StockRow
                  symbol={symbol}
                  aiModels={models}
                  isSelected={userSymbols.includes(symbol)}
                  onToggle={toggleSymbol}
                  inCard
                />
              </React.Fragment>
            ))
          )}
        </div>
      </div>

      {/* Section 2: Investor-type curated picks — hidden if all picks overlap with AI holdings */}
      {curatedPicks.length > 0 && (
        <>
          <div>
            <SectionLabel>{sectionTwoHeader}</SectionLabel>
            <div
              css={css`
                border-radius: 8px;
                border: 0.5px solid ${colors.cloud};
                overflow: hidden;
                html[data-theme='dark'] & {
                  border-color: ${colors.tungsten};
                }
              `}
              data-testid="curated-picks-section"
            >
              {curatedPicks.map(({ symbol, name }, idx) => (
                <React.Fragment key={symbol}>
                  {idx > 0 && (
                    <div
                      css={css`
                        height: 0.5px;
                        background: ${colors.cloud};
                        html[data-theme='dark'] & {
                          background: ${colors.tungsten};
                        }
                      `}
                    />
                  )}
                  <StockRow
                    symbol={symbol}
                    name={name}
                    isSelected={userSymbols.includes(symbol)}
                    onToggle={toggleSymbol}
                    inCard
                  />
                </React.Fragment>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OnboardingTopIdeas;
